import {human} from '@github-ui/formatters'
import {ListItem} from '@github-ui/list-view'
import {
  forksListPath,
  orgTopicReposPath,
  repoIssuesPath,
  repoPullRequestsPath,
  repositoryParticipationPath,
  repositoryPath,
  repoStargazersPath,
} from '@github-ui/paths'
import type {PrimaryLanguage, RepositoryItem} from '@github-ui/repos-list-shared'
import {ObservableBox} from '@github-ui/use-sticky-header/ObservableBox'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {
  GitPullRequestIcon,
  type Icon,
  IssueOpenedIcon,
  LawIcon,
  RepoCloneIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  StarIcon,
} from '@primer/octicons-react'
import {type BaseStylesProps, Box, Label, Link, Octicon, RelativeTime, Text, Truncate} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useRef, useState} from 'react'

import {LanguageCircle} from './LanguageCircle'
import {Sparkline} from './Sparkline'

interface Props {
  repo: RepositoryItem
}

const smallScreenOnly: BaseStylesProps = {display: ['block', 'none']}
const hideOnSmallScreen: BaseStylesProps = {display: ['none', 'block']}
const hideOnSmallScreenInline: BaseStylesProps = {display: ['none', 'inline']}
const unfurlOnSmallScreen: BetterSystemStyleObject = {
  gridTemplateRows: ['repeat(3, auto)', 'auto'],
  gridTemplateAreas: [
    '"primary" "main-content" "metadata"',
    '"selection status primary main-content metadata actions"',
  ],
  gridTemplateColumns: ['auto', 'min-content min-content minmax(2em, auto) 1fr minmax(0, max-content) min-content'],
  pb: [2, 0],
  pl: [3, 0],
  pr: [3, 0],
}

export function CompactListItem({repo}: Props) {
  const {name, owner, description, lastUpdated} = repo
  return (
    <ListItem
      sx={unfurlOnSmallScreen}
      metadata={[
        <ListItem.Metadata key="language" alignment="left" sx={{display: 'flex', width: ['auto', '90px']}}>
          {repo.primaryLanguage ? (
            <LanguageLabel truncate {...repo} />
          ) : (
            <Text sx={{whiteSpace: 'nowrap'}}>No language</Text>
          )}
        </ListItem.Metadata>,
        <ListItem.Metadata key="forks" alignment="left" sx={{width: '45px'}}>
          <ForksLabel {...repo} />
        </ListItem.Metadata>,
        <ListItem.Metadata key="stars" alignment="left" sx={{display: 'flex', width: ['auto', '45px']}}>
          <StarsLabel {...repo} />
        </ListItem.Metadata>,
        <ListItem.Metadata key="time" alignment="left" sx={{display: ['flex', 'none'], width: ['auto', '45px']}}>
          <RelativeTime datetime={lastUpdated.timestamp} format="micro" />
        </ListItem.Metadata>,
      ]}
      title={<ListItem.Title value={name} containerSx={{pb: [1, 2]}} href={repositoryPath({owner, repo: name})} />}
    >
      <ListItem.LeadingContent sx={{...hideOnSmallScreen}}>
        <ListItem.LeadingVisual color="fg.muted" icon={getRepoIcon(repo)} />
      </ListItem.LeadingContent>

      <ListItem.MainContent>
        {description && (
          <>
            <ListItem.Description sx={{pb: 2, ...smallScreenOnly}}>{description}</ListItem.Description>
            <ListItem.Description sx={{minWidth: 0, width: '100%', ...hideOnSmallScreen}}>
              <Truncate inline title={description} sx={{maxWidth: '100%'}}>
                {description}
              </Truncate>
            </ListItem.Description>
          </>
        )}
      </ListItem.MainContent>
    </ListItem>
  )
}

const sparklineWidth = 100
export function DefaultListItem({repo}: Props) {
  const {name, type, owner, description, lastUpdated, license, topicNames} = repo

  return (
    <ListItem
      title={
        <ListItem.Title
          value={name}
          href={repositoryPath({owner, repo: name})}
          containerSx={{display: 'inline-flex', alignItems: 'center'}}
          headerSx={{pl: 2}}
        >
          <Box sx={hideOnSmallScreenInline}>
            <Label size="small" sx={{mx: 2}} variant="secondary">
              {type}
            </Label>
          </Box>
          <ParticipationSparkline repo={repo} />
        </ListItem.Title>
      }
    >
      <ListItem.MainContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            color: 'fg.muted',
            gap: 2,
            width: ['100%', '100%', `calc(100% - ${sparklineWidth}px)`],
            pl: 2,
          }}
        >
          <ListItem.Description>{description}</ListItem.Description>

          {topicNames.length > 0 && (
            <Box sx={{...hideOnSmallScreen}}>
              <TopicsLabels {...repo} />
            </Box>
          )}

          <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: 0}}>
            <LanguageLabel {...repo} />

            {license && (
              <Box sx={{...hideOnSmallScreen}}>
                <LicenseLabel {...repo} />
              </Box>
            )}

            <Box>
              <ForksLabel {...repo} />
            </Box>

            <StarsLabel {...repo} />

            {repo.issueCount !== null && (
              <Box>
                <IssuesLabel issueCount={repo.issueCount} owner={repo.owner} name={repo.name} />
              </Box>
            )}

            {repo.pullRequestCount !== null && (
              <Box>
                <PullRequestsLabel pullRequestCount={repo.pullRequestCount} owner={repo.owner} name={repo.name} />
              </Box>
            )}

            <Text sx={{...hideOnSmallScreen}}>•</Text>

            <Text sx={{overflow: 'hidden', whiteSpace: 'nowrap', ...hideOnSmallScreen}}>
              {lastUpdated.hasBeenPushedTo ? 'Updated' : 'Created'} <RelativeTime datetime={lastUpdated.timestamp} />
            </Text>

            <RelativeTime datetime={lastUpdated.timestamp} format="micro" sx={{...smallScreenOnly}} />
          </Box>
        </Box>
      </ListItem.MainContent>
    </ListItem>
  )
}

function getRepoIcon(repo: RepositoryItem) {
  const {isFork, type} = repo
  let icon
  if (isFork) {
    icon = RepoForkedIcon
  } else if (type.includes('mirror')) {
    icon = RepoCloneIcon
  } else if (type === 'Private' || type === 'Internal') {
    icon = RepoLockedIcon
  } else {
    icon = RepoIcon
  }

  return icon
}

function TopicsLabels({
  topicNames,
  topicsNotShown,
  owner,
}: {
  topicNames: string[]
  topicsNotShown: number
  owner: string
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {topicNames.map(topic => (
        <Link key={topic} href={orgTopicReposPath({topic, org: owner})} data-testid={`${topic}-topic-link`}>
          <Label
            size="small"
            sx={{
              border: '0',
              color: 'accent.fg',
              backgroundColor: 'accent.subtle',
              '&:hover': {
                color: 'fg.onEmphasis',
                backgroundColor: 'accent.fg',
              },
            }}
          >
            {topic}
          </Label>
        </Link>
      ))}
      {topicsNotShown > 0 && (
        <Label size="small" variant="secondary">
          + {topicsNotShown}
        </Label>
      )}
    </Box>
  )
}

function LanguageLabel({primaryLanguage, truncate}: {primaryLanguage: PrimaryLanguage | null; truncate?: boolean}) {
  if (!primaryLanguage) {
    return null
  }

  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
      <LanguageCircle color={primaryLanguage.color} />
      {truncate ? (
        <Truncate inline title={primaryLanguage.name} sx={{maxWidth: 70}}>
          {primaryLanguage.name}
        </Truncate>
      ) : (
        <Text sx={{whiteSpace: 'nowrap'}}>{primaryLanguage.name}</Text>
      )}
    </Box>
  )
}

function LicenseLabel({license}: {license: string | null}) {
  if (!license) {
    return null
  }

  return <IconLabel content={license} icon={LawIcon} />
}

function StarsLabel({starsCount, owner, name}: {starsCount: number; owner: string; name: string}) {
  const count = human(starsCount)
  const unit = count === '1' ? 'star' : 'stars'
  const label = `${count} ${unit}`
  return <IconLabel aria-label={label} content={count} icon={StarIcon} href={repoStargazersPath(owner, name)} />
}

function ForksLabel({forksCount, owner, name}: {forksCount: number; owner: string; name: string}) {
  const count = human(forksCount)
  const unit = count === '1' ? 'fork' : 'forks'
  const label = `${count} ${unit}`
  return (
    <IconLabel aria-label={label} content={count} icon={RepoForkedIcon} href={forksListPath({owner, repo: name})} />
  )
}

function IssuesLabel({issueCount, owner, name}: {issueCount: number; owner: string; name: string}) {
  const count = human(issueCount)
  const unit = count === '1' ? 'issue' : 'issues'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'issue-count'}
      content={count}
      icon={IssueOpenedIcon}
      href={repoIssuesPath(owner, name)}
    />
  )
}

function PullRequestsLabel({pullRequestCount, owner, name}: {pullRequestCount: number; owner: string; name: string}) {
  const count = human(pullRequestCount)
  const unit = count === '1' ? 'pull request' : 'pull requests'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'pull-request-count'}
      content={count}
      icon={GitPullRequestIcon}
      href={repoPullRequestsPath(owner, name)}
    />
  )
}

const labelContainerSx = {display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap'}
function IconLabel({
  icon,
  href,
  content,
  ...props
}: {
  icon: Icon
  href?: string
  content: string
  ['aria-label']?: string
}) {
  if (href) {
    return (
      <Link {...props} muted={true} sx={labelContainerSx} href={href}>
        <Octicon icon={icon} />
        {content}
      </Link>
    )
  }

  return (
    <Box {...props} sx={labelContainerSx}>
      <Octicon icon={icon} />
      {content}
    </Box>
  )
}

function ParticipationSparkline({repo}: Props) {
  const [participation, setParticipation] = useState(repo.participation)
  const isFetchingRef = useRef(false)

  async function fetchParticipation() {
    if (participation || isFetchingRef.current) return
    isFetchingRef.current = true

    const response = await verifiedFetchJSON(repositoryParticipationPath({owner: repo.owner, repo: repo.name}))
    if (response.ok) {
      try {
        setParticipation(await response.json())
      } catch (e) {
        // Don't set any participation data in the event of an invalid JSON response
      }
    }
  }

  const [observeRow, unobserveRow] = useIntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) {
      fetchParticipation()
    }
  })

  return participation ? (
    <Box sx={{ml: 'auto', pr: 2}}>
      <Sparkline label="Past year of activity" uniqueKey={repo.name} points={participation} width={sparklineWidth} />
    </Box>
  ) : (
    <ObservableBox role="presentation" onObserve={observeRow} onUnobserve={unobserveRow} />
  )
}

try{ CompactListItem.displayName ||= 'CompactListItem' } catch {}
try{ DefaultListItem.displayName ||= 'DefaultListItem' } catch {}
try{ TopicsLabels.displayName ||= 'TopicsLabels' } catch {}
try{ LanguageLabel.displayName ||= 'LanguageLabel' } catch {}
try{ LicenseLabel.displayName ||= 'LicenseLabel' } catch {}
try{ StarsLabel.displayName ||= 'StarsLabel' } catch {}
try{ ForksLabel.displayName ||= 'ForksLabel' } catch {}
try{ IssuesLabel.displayName ||= 'IssuesLabel' } catch {}
try{ PullRequestsLabel.displayName ||= 'PullRequestsLabel' } catch {}
try{ IconLabel.displayName ||= 'IconLabel' } catch {}
try{ ParticipationSparkline.displayName ||= 'ParticipationSparkline' } catch {}